/**
 * Pinia store for UI preferences management
 * Handles UI scaling, density, and accessibility settings
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export type UIScale = 'small' | 'medium' | 'large'
export type UIDensity = 'compact' | 'default' | 'comfortable'

export interface UIScaleConfig {
  label: string
  fontSize: string
  btnHeight: string
  cardPadding: string
  tableRowHeight: string
  iconSize: string
}

export const uiScaleConfigs: Record<UIScale, UIScaleConfig> = {
  small: {
    label: 'Malé',
    fontSize: '14px',
    btnHeight: '36px',
    cardPadding: '12px',
    tableRowHeight: '40px',
    iconSize: '20px'
  },
  medium: {
    label: 'Střední',
    fontSize: '16px',
    btnHeight: '40px',
    cardPadding: '16px',
    tableRowHeight: '48px',
    iconSize: '24px'
  },
  large: {
    label: 'Velké',
    fontSize: '18px',
    btnHeight: '48px',
    cardPadding: '20px',
    tableRowHeight: '56px',
    iconSize: '28px'
  },
}

export const densityConfigs: Record<UIDensity, string> = {
  compact: 'Kompaktní',
  default: 'Výchozí',
  comfortable: 'Pohodlné'
}

const STORAGE_KEY = 'eurodrive-ui-preferences'

export const usePreferencesStore = defineStore('preferences', () => {
  // State
  const uiScale = ref<UIScale>('medium')
  const density = ref<UIDensity>('default')
  const initialized = ref(false)

  // Getters
  const currentScaleConfig = computed(() => uiScaleConfigs[uiScale.value])
  const scaleOptions = computed(() =>
    Object.entries(uiScaleConfigs).map(([key, config]) => ({
      id: key as UIScale,
      label: config.label
    }))
  )
  const densityOptions = computed(() =>
    Object.entries(densityConfigs).map(([key, label]) => ({
      id: key as UIDensity,
      label
    }))
  )

  // Actions
  const loadPreferences = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed.uiScale && uiScaleConfigs[parsed.uiScale as UIScale]) {
          uiScale.value = parsed.uiScale
        }
        if (parsed.density && densityConfigs[parsed.density as UIDensity]) {
          density.value = parsed.density
        }
      }
    } catch (e) {
      console.warn('Failed to load UI preferences:', e)
    }
    initialized.value = true
    applyPreferences()
  }

  const savePreferences = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        uiScale: uiScale.value,
        density: density.value
      }))
    } catch (e) {
      console.warn('Failed to save UI preferences:', e)
    }
  }

  const applyPreferences = () => {
    const config = currentScaleConfig.value
    const root = document.documentElement

    // Apply root font size for rem-based scaling
    root.style.fontSize = config.fontSize

    // Apply CSS custom properties
    root.style.setProperty('--ui-btn-height', config.btnHeight)
    root.style.setProperty('--ui-card-padding', config.cardPadding)
    root.style.setProperty('--ui-table-row-height', config.tableRowHeight)
    root.style.setProperty('--ui-icon-size', config.iconSize)

    // Apply scale and density as data attributes for CSS targeting
    root.setAttribute('data-ui-scale', uiScale.value)
    root.setAttribute('data-ui-density', density.value)
  }

  const setScale = (scale: UIScale) => {
    uiScale.value = scale
    applyPreferences()
    savePreferences()
  }

  const setDensity = (newDensity: UIDensity) => {
    density.value = newDensity
    applyPreferences()
    savePreferences()
  }

  const resetToDefaults = () => {
    uiScale.value = 'medium'
    density.value = 'default'
    applyPreferences()
    savePreferences()
  }

  // Watch for changes and auto-save
  watch([uiScale, density], () => {
    if (initialized.value) {
      applyPreferences()
      savePreferences()
    }
  })

  return {
    // State
    uiScale,
    density,
    initialized,

    // Getters
    currentScaleConfig,
    scaleOptions,
    densityOptions,

    // Actions
    loadPreferences,
    savePreferences,
    applyPreferences,
    setScale,
    setDensity,
    resetToDefaults
  }
})
